import React from 'react';
import { RefreshCw, Plus, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const PER_PAGE = 10;

export const ROLE_VARIANTS = {
  admin:      'info',
  direction:  'transit',
  finance:    'paid',
  operations: 'preparing',
  achats:     'warning',
};

export const INVITE_DEFAULTS = { email: '', role: '' };

export function fmtDate(d, locale) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export function fmtAmount(amount, currency = 'EUR', locale = 'fr') {
  if (amount == null) return '—';
  return (
    new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) +
    ' ' + currency
  );
}

export function fmtRelative(dateStr) {
  if (!dateStr) return '—';
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return "À l'instant";
  if (mins < 60)  return `il y a ${mins} min`;
  if (hours < 24) return `il y a ${hours} h`;
  if (days < 7)   return `il y a ${days} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export function userInitials(u) {
  if (u?.initials) return u.initials.toUpperCase();
  if (u?.fullname?.trim()) {
    return u.fullname.split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();
  }
  return (u?.email ?? '?')[0].toUpperCase();
}

export function PageHeader({ title, sub, onRefresh, loading, onAdd, addLabel, extraActions }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
        <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>
      </div>
      <div className="flex items-center gap-2">
        {extraActions}
        <button
          onClick={onRefresh}
          disabled={loading}
          className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
        >
          <RefreshCw size={13} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
        {onAdd && (
          <button
            onClick={onAdd}
            className="h-8 px-3 flex items-center gap-1.5 rounded-lg bg-primary text-white text-[12px] font-medium hover:opacity-90 transition"
          >
            <Plus size={13} strokeWidth={2} />
            {addLabel}
          </button>
        )}
      </div>
    </div>
  );
}

export function RowActions({ onEdit, onDelete, deleteDisabled = false }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        title={t('common.edit')}
        className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
      >
        <Pencil size={12} strokeWidth={1.75} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title={t('common.delete')}
        disabled={deleteDisabled}
        className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 disabled:pointer-events-none transition"
      >
        <Trash2 size={12} strokeWidth={1.75} />
      </button>
    </div>
  );
}

export function FormFooter({ onCancel, onSave, saving }) {
  const { t } = useTranslation();
  return (
    <>
      <button
        onClick={onCancel}
        className="h-9 px-4 rounded-lg border border-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-50 transition"
      >
        {t('common.cancel')}
      </button>
      <button
        onClick={onSave}
        disabled={saving}
        className="h-9 px-4 rounded-lg bg-primary text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition"
      >
        {saving ? t('common.saving') : t('common.save')}
      </button>
    </>
  );
}

export function GlobalError({ message }) {
  return (
    <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5 text-[12.5px] text-rose-700">
      {message}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
      <span>{message}</span>
      <button onClick={onRetry} className="font-medium underline text-[12px]">{t('common.retry')}</button>
    </div>
  );
}
